/**
 * MentionList Component
 * 
 * A suggestion list for @mentions in the TipTap editor.
 * Displays both JIRA and Slack users with visual differentiation.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Chip,
} from '@mui/material';
import {
  Person as PersonIcon,
} from '@mui/icons-material';

// Slack icon SVG
const SlackIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
    <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
  </svg>
);

// JIRA icon SVG
const JiraIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.571 11.513H0a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057A5.215 5.215 0 0 0 12.575 24V12.518a1.005 1.005 0 0 0-1.005-1.005zm5.723-5.756H5.736a5.215 5.215 0 0 0 5.215 5.214h2.129v2.058a5.218 5.218 0 0 0 5.215 5.214V6.758a1.001 1.001 0 0 0-1.001-1.001zM23.013 0H11.455a5.215 5.215 0 0 0 5.215 5.215h2.129v2.057A5.215 5.215 0 0 0 24 12.483V1.005A1.004 1.004 0 0 0 23.013 0z"/>
  </svg>
);

const MentionList = ({ items = [], command }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Reset selection when items change
  useEffect(() => {
    setSelectedIndex(0);
  }, [items]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((event) => {
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + items.length) % items.length);
      return true;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % items.length);
      return true;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      if (items[selectedIndex]) {
        selectItem(selectedIndex);
      }
      return true;
    }

    return false;
  }, [items, selectedIndex]);

  // Attach keyboard listener
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const selectItem = (index) => {
    const item = items[index];
    if (item && command) {
      command({ id: item.id, label: item.label });
    }
  };

  if (!items.length) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No users found
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 0.5 }}>
      {items.map((item, index) => (
        <Box
          key={item.id}
          onClick={() => selectItem(index)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1,
            cursor: 'pointer',
            backgroundColor: index === selectedIndex ? 'action.selected' : 'transparent',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          {/* Avatar */}
          <Avatar
            src={item.avatar}
            sx={{ width: 28, height: 28, fontSize: '0.75rem' }}
          >
            {item.label?.[0]?.toUpperCase() || <PersonIcon fontSize="small" />}
          </Avatar>

          {/* User info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {item.label}
            </Typography>
            {item.email && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                }}
              >
                {item.email}
              </Typography>
            )}
          </Box>

          {/* Source indicator */}
          {item.source && (
            <Chip
              size="small"
              icon={
                item.source === 'slack' ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', color: '#4A154B' }}>
                    <SlackIcon />
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', color: '#0052CC' }}>
                    <JiraIcon />
                  </Box>
                )
              }
              label={item.source === 'slack' ? 'Slack' : 'Jira'}
              sx={{
                height: 20,
                fontSize: '0.65rem',
                backgroundColor: item.source === 'slack' 
                  ? 'rgba(74, 21, 75, 0.08)' 
                  : 'rgba(0, 82, 204, 0.08)',
                color: item.source === 'slack' ? '#4A154B' : '#0052CC',
                '& .MuiChip-icon': {
                  marginLeft: '4px',
                },
                '& .MuiChip-label': {
                  paddingLeft: '4px',
                  paddingRight: '6px',
                },
              }}
            />
          )}
        </Box>
      ))}
    </Box>
  );
};

export default MentionList;
